import type { GatewayBrowserClient } from "../gateway.ts";

/** Shape of a skill parameter as returned by the gateway. */
export type SkillParameterInfo = {
  name: string;
  label: string;
  description?: string;
  type: "string" | "url" | "apiKey" | "secret" | "enum";
  required?: boolean;
  options?: string[];
  default?: string;
  envVar?: string;
  configPath?: string;
};

/** Summary entry returned by skills.listAll. */
export type SkillSummary = {
  key: string;
  name: string;
  description: string;
  emoji: string;
  enabled: boolean;
  hasParameters: boolean;
  parameterCount: number;
};

/** Result from skills.getParameters. */
export type SkillParametersResult = {
  skillKey: string;
  name: string;
  parameters: SkillParameterInfo[];
  currentConfig: {
    enabled?: boolean;
    apiKey?: string;
    env?: Record<string, string>;
    config?: Record<string, unknown>;
  };
};

/** State slice for the skills settings panel. */
export type SkillsSettingsState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  skillsSettingsLoading: boolean;
  skillsSettingsError: string | null;
  skillsSettingsList: SkillSummary[];
  skillsSettingsSelectedKey: string | null;
  skillsSettingsParams: SkillParametersResult | null;
  skillsSettingsParamsLoading: boolean;
  skillsSettingsParamsError: string | null;
  skillsSettingsEdits: Record<string, string>;
  skillsSettingsSaving: string | null;
  skillsSettingsSaveMessage: { kind: "success" | "error"; message: string } | null;
};

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

/** Load the list of all skills for the dropdown. */
export async function loadSkillsSettingsList(state: SkillsSettingsState) {
  if (!state.client || !state.connected) return;
  if (state.skillsSettingsLoading) return;
  state.skillsSettingsLoading = true;
  state.skillsSettingsError = null;
  try {
    const res = await state.client.request<{ skills: SkillSummary[] }>("skills.listAll", {});
    state.skillsSettingsList = res?.skills ?? [];
  } catch (err) {
    state.skillsSettingsError = getErrorMessage(err);
  } finally {
    state.skillsSettingsLoading = false;
  }
}

/** Load parameters for a specific skill. */
export async function loadSkillParameters(state: SkillsSettingsState, skillKey: string) {
  if (!state.client || !state.connected) return;
  state.skillsSettingsParamsLoading = true;
  state.skillsSettingsParamsError = null;
  state.skillsSettingsEdits = {};
  state.skillsSettingsSaveMessage = null;
  try {
    const res = await state.client.request<SkillParametersResult>("skills.getParameters", {
      skillKey,
    });
    state.skillsSettingsParams = res ?? null;
    // Pre-fill edits from current config
    if (res) {
      const edits: Record<string, string> = {};
      for (const param of res.parameters) {
        let currentValue = "";
        if (param.envVar && res.currentConfig.env?.[param.envVar]) {
          currentValue = res.currentConfig.env[param.envVar];
        } else if (param.type === "apiKey" && res.currentConfig.apiKey) {
          currentValue = res.currentConfig.apiKey;
        } else if (param.configPath && res.currentConfig.config?.[param.configPath] != null) {
          currentValue = String(res.currentConfig.config[param.configPath]);
        } else if (param.default) {
          currentValue = param.default;
        }
        edits[param.name] = currentValue;
      }
      state.skillsSettingsEdits = edits;
    }
  } catch (err) {
    state.skillsSettingsParamsError = getErrorMessage(err);
  } finally {
    state.skillsSettingsParamsLoading = false;
  }
}

/** Select a skill from the dropdown. */
export async function selectSkill(state: SkillsSettingsState, skillKey: string | null) {
  state.skillsSettingsSelectedKey = skillKey;
  state.skillsSettingsParams = null;
  state.skillsSettingsEdits = {};
  state.skillsSettingsSaveMessage = null;
  if (skillKey) {
    await loadSkillParameters(state, skillKey);
  }
}

/** Update a parameter edit value. */
export function updateSkillParamEdit(state: SkillsSettingsState, paramName: string, value: string) {
  state.skillsSettingsEdits = { ...state.skillsSettingsEdits, [paramName]: value };
}

/** Save a single parameter value. */
export async function saveSkillParameter(
  state: SkillsSettingsState,
  skillKey: string,
  paramName: string,
) {
  if (!state.client || !state.connected) return;
  const value = state.skillsSettingsEdits[paramName] ?? "";
  state.skillsSettingsSaving = paramName;
  state.skillsSettingsSaveMessage = null;
  try {
    await state.client.request("skills.applyParameter", {
      skillKey,
      paramName,
      value,
    });
    state.skillsSettingsSaveMessage = { kind: "success", message: `${paramName} saved` };
    // Reload parameters to reflect new state
    await loadSkillParameters(state, skillKey);
  } catch (err) {
    state.skillsSettingsSaveMessage = { kind: "error", message: getErrorMessage(err) };
  } finally {
    state.skillsSettingsSaving = null;
  }
}

/** Save all parameter values at once. */
export async function saveAllSkillParameters(state: SkillsSettingsState, skillKey: string) {
  if (!state.client || !state.connected) return;
  if (!state.skillsSettingsParams) return;
  state.skillsSettingsSaving = "__all__";
  state.skillsSettingsSaveMessage = null;
  try {
    for (const param of state.skillsSettingsParams.parameters) {
      const value = state.skillsSettingsEdits[param.name] ?? "";
      if (!value && !param.required) continue;
      await state.client.request("skills.applyParameter", {
        skillKey,
        paramName: param.name,
        value,
      });
    }
    state.skillsSettingsSaveMessage = { kind: "success", message: "All parameters saved" };
    await loadSkillParameters(state, skillKey);
  } catch (err) {
    state.skillsSettingsSaveMessage = { kind: "error", message: getErrorMessage(err) };
  } finally {
    state.skillsSettingsSaving = null;
  }
}

/** Toggle skill enabled/disabled. */
export async function toggleSkillSettingsEnabled(
  state: SkillsSettingsState,
  skillKey: string,
  enabled: boolean,
) {
  if (!state.client || !state.connected) return;
  state.skillsSettingsSaving = "__toggle__";
  try {
    await state.client.request("skills.update", { skillKey, enabled });
    state.skillsSettingsSaveMessage = {
      kind: "success",
      message: enabled ? "Skill enabled" : "Skill disabled",
    };
    await loadSkillsSettingsList(state);
    if (state.skillsSettingsSelectedKey === skillKey) {
      await loadSkillParameters(state, skillKey);
    }
  } catch (err) {
    state.skillsSettingsSaveMessage = { kind: "error", message: getErrorMessage(err) };
  } finally {
    state.skillsSettingsSaving = null;
  }
}
